<template>
  <SectionContainer v-if="faqs.length || isLoading || status === 'error'">
    <div class="company-wall-municipality-faqs">
      <SHeading
        class="company-wall-municipality-faqs__title"
        level="2"
        size="2"
      >
        {{ tr("main_site.company_wall.municipality.faq.title") }}
      </SHeading>

      <PAccordion
        v-if="faqs.length"
        class="company-wall-municipality-faqs__list"
      >
        <PAccordionPanel
          v-for="(faq, index) in faqs"
          :key="faq.question"
          class="company-wall-municipality-faqs__panel"
          :value="String(index)"
        >
          <PAccordionHeader
            class="company-wall-municipality-faqs__panel__header"
          >
            {{ faq.question }}
          </PAccordionHeader>
          <PAccordionContent
            class="company-wall-municipality-faqs__panel__content"
          >
            <p>{{ faq.answer }}</p>
          </PAccordionContent>
        </PAccordionPanel>
      </PAccordion>

      <div
        v-else-if="isLoading"
        class="company-wall-municipality-faqs__skeletons"
      >
        <SInputSkeleton v-for="index in faqLimit" :key="index" />
      </div>

      <SErrorMessage
        v-else-if="status === 'error'"
        retry
        size="lg"
        @retry="refresh"
      />
    </div>
  </SectionContainer>
</template>

<script setup lang="ts">
import { useQuery } from "@pinia/colada";
import { computed } from "vue";

import {
  PAccordion,
  PAccordionContent,
  PAccordionHeader,
  PAccordionPanel,
  SErrorMessage,
  SHeading,
  SInputSkeleton,
} from "@solvari/components";
import {
  injectI18nLocale,
  useI18nMainSiteCompanyWall,
} from "@solvari/translations";

import { useGeoProductFaqs } from "~/features/company-wall/api/faqs.api.ts";
import SectionContainer from "~/shared/components/SectionContainer.vue";

import { useLocalSearchTranslations } from "../../composables/useLocalSearchTranslations.ts";

const faqLimit = 5;

const props = defineProps<{
  geoProductSlug: string;
  municipality: string;
  province: string;
}>();

const localeCode = injectI18nLocale().value;

const { tr } = useLocalSearchTranslations(useI18nMainSiteCompanyWall, {
  localeCode,
  geoProductSlug: props.geoProductSlug,
  municipality: props.municipality,
  province: props.province,
});

const { data, status, refresh, isLoading } = useQuery(
  useGeoProductFaqs({
    geoProductCategorySlug: props.geoProductSlug,
    localeCode,
    municipalitySlug: props.municipality,
  }),
);

const faqs = computed(() => (data.value ?? []).slice(0, faqLimit));
</script>

<style lang="postcss">
.company-wall-municipality-faqs {
  &__title {
    @apply mb-4 text-center;
  }

  &__list {
    @apply w-full;
  }

  &__skeletons {
    @apply flex w-full flex-col gap-2;
  }
}
</style>
